import React, { PureComponent } from 'react';
import * as EmailValidator from 'email-validator';
import { withTranslation } from 'react-i18next';

import { getCurrentUserId } from '../services/store-service';
import { removeDupes } from '../utils/helpers';

import { RolesAndPermissionsContext } from 'context/RolesAndPermissionsContext';

import Popout from './popout';
import AvatarBase from './avatar-base';
import UserSearchField from './search/user-search-field';
import SelectionList from './selection-list';
import SelectionListItem from './selection-list-item';
import FieldDefault from './field-default';
import UserPermissionSelector from './user-permission-selector';

const UserEntry = ({ user, permissions, isSelf, t, onClick, onPermissionSelect, setPermissionCtrl }) => {
    const setCtrl = ctrl => setPermissionCtrl(user.id, ctrl);

    const handleClick = () => {
        if (!permissions || !permissions.length) {
            return;
        }

        onClick && onClick(user);
    }

    const handleMouseDown = () => {
        if (permissions && permissions.length) {
            return;
        }

        onClick && onClick(user);
    }

    const handlePermissionSelect = (userId, permission) => {
        onPermissionSelect && onPermissionSelect(userId, permission);
    }

    return (
        <div className="user-entry" onClick={handleClick} onMouseDown={handleMouseDown}>
            <div className="user-entry-head">
                <AvatarBase size="medium" color={user.color} foregroundColor={user.secondary_color} image={user.avatar} text={user.initials} />
            </div>

            <div className="user-entry-body">
                <div className="user-entry-body-main">
                    <small>
                        { user.name || user.email }

                        { isSelf ? ` (${t('you')})` : '' }
                    </small>
                </div>
            </div>

            {
                permissions && permissions.length &&
                    <div className="user-entry-foot visible">
                        <button className="btn btn-action btn-double-action">
                            <span className="main-action">
                                +
                            </span>

                            <span className="secondary-action">
                                <i className="material-icons">keyboard_arrow_down</i>
                            </span>
                        </button>

                        <UserPermissionSelector
                            successText={t('Invite')}
                            userId={user.id}
                            permissions={permissions}
                            onSuccess={handlePermissionSelect}
                            provideController={setCtrl}
                        />
                    </div>
            }
        </div>
    )
}

class FieldUser extends PureComponent {
    static contextType = RolesAndPermissionsContext;

    static defaultProps = {
        popoutFoot: {
            enabled: true
        },
        popoutClassName: 'dark-arrow',
        email: '',
        suggestions: [],
        namePlaceholder: 'optional'
    }

    constructor(props) {
        super(props);

        this.state = {
            focused: false,
            error: false,
            searchTerm: props.email,
            name: props.name,
            filteredSuggestions: []
        };

        this.popoutCtrl = null;
        this.fieldCtrl = null;

        this.permissionCtrls = {};

        this.role = this.props.role;
        this.email = this.props.email;
        this.selectedUser = null;
    }

    handleChange = email => {
        this.updateValue(email);
    }

    handleNameChange = name => {
        this.setState({ name }, () => {
            this.emitChange();
        });
    }

    handleFocus = () => {
        this.setState({
            focused: true
        }, () => setTimeout(() => {
            if (this.email !== '' || this.props.showEmpty) {
                this.showSuggestions();
            }
        }, 200));
    }

    handleBlur = () => {
        this.setState({
            error: this.email !== '' && !EmailValidator.validate(this.email),
            focused: false
        });

        // Add delay so the click can be handled
        setTimeout(() => {
            this.hideSuggestions();
        });
    }

    handleKeyDown = ({ which }) => {
        if (which === 13) {
            setTimeout(() => this.hideSuggestions());
        }
    }

    handleSuggestionSelect = index => {
        this.selectUser(this.state.filteredSuggestions[index]);
    }

    handleSuggestionClick = user => {
        this.selectUser(user);
    }

    updateValue(value) {
        if (this.state.error) {
            this.setState({
                error: value !== '' && !EmailValidator.validate(value)
            });
        }

        this.email = value.toLowerCase().trim();

        this.setState({
            searchTerm: this.email
        });

        this.updateFilteredSuggestions();

        const fieldUser = this.props.suggestions.find(user => user.email === this.email);

        if (fieldUser && (!this.selectedUser || this.selectedUser.id !== fieldUser.id)) {
            this.selectedUser = fieldUser;

            if (this.props.onSelect && !this.props.onSelect(fieldUser)) {
                return;
            }
        }

        this.emitChange();
    }

    selectUser(user) {
        if (!user) {
            return;
        }

        if (user.id && this.permissionCtrls[user.id]) {
            this.permissionCtrls[user.id].show();

            return;
        }

        const email = user.email;

        this.setState({
            searchTerm: email
        });

        this.updateValue(email);
        this.fieldCtrl && this.fieldCtrl.blur()
    }

    showSuggestions() {
        if (!this.props.suggestions.length) {
            return;
        }

        this.setState({
            suggestionsVisible: true
        });

        this.popoutCtrl && this.popoutCtrl.show();
    }

    hideSuggestions() {
        this.popoutCtrl && this.popoutCtrl.hide();

        this.setState({
            suggestionsVisible: false
        });
    }

    handleRoleChange = role => {
        this.role = role;

        this.emitChange();
    }

    handleInviteClick = () => {
        if (this.props.permissions) {
            this.invitePermissionCtrl.show();
            return;
        }

        this.props.onInvite && this.props.onInvite({
            email: this.state.searchTerm,
            role: this.role || (this.roles && this.roles[0].id) || 'regular'
        });
    }

    handlePermissionSelect = (userId, permission) => {
        if (userId) {
            this.props.onSelect({
                id: userId,
                permission
            });

            return;
        }

        this.props.onInvite({
            permission,
            email: this.state.searchTerm,
            role: this.role || (this.roles && this.roles[0].id),
        });
    }

    updateFilteredSuggestions() {
        const filteredSuggestions = removeDupes((this.props.suggestions || []).filter(user =>
            (user.name && user.name.toLowerCase().indexOf(this.email) > -1) ||
            (user.email && user.email.toLowerCase().indexOf(this.email) > -1)
        ));

        this.setState({ filteredSuggestions });

        if ((this.email !== '' || this.props.showEmpty) && this.state.focused && filteredSuggestions.length) {
            this.showSuggestions();
        } else {
            this.hideSuggestions();
        }
    }

    emitChange() {
        if (!this.props.onChange) {
            return;
        }

        if (this.selectedUser && this.selectedUser.email === this.email) {
            this.props.onChange(this.selectedUser); 

            return;
        }

        this.props.onChange({
            email: this.email,
            name: this.state.name,
            isValid: EmailValidator.validate(this.email) && !this.nameIsEmail,
            role: this.role
        });
    }

    validateNewUserEmail(email) {
        return EmailValidator.validate(email) && !this.props.suggestions.find(user => user.email === email);
    }

    setPermissionCtrl = (userId, ctrl) => this.permissionCtrls[userId] = ctrl

    setPopoutCtrl = ctrl => this.popoutCtrl = ctrl;
    setInvitePermissionCtrl = ctrl => this.invitePermissionCtrl = ctrl;

    get nameIsEmail() {
        return EmailValidator.validate(this.state.name);
    }

    get roles() {
        if (this.props.alwaysShowRoles || (EmailValidator.validate(this.state.searchTerm) && !this.state.filteredSuggestions.length && this.props.emailMatchText)) {
            return this.props.roles;
        }

        return null;
    }

    get suggestionsElement() {
        if (this.state.searchTerm === '' && !this.props.showEmpty) {
            return null;
        }

        const { t } = this.props;

        if (this.state.searchTerm && !this.state.filteredSuggestions.length && this.props.noMatchText) {
            if (EmailValidator.validate(this.state.searchTerm) && this.context?.canUser('inviteToProject')) {
                return (
                    <div className="user-selector-nomatch">
                        { this.props.emailMatchText }

                        <div className="popout-foot">
                            <div className="popout-actions">
                                <button className="btn btn-secondary btn-small" onClick={this.props.onCancel}>{ t('Cancel') }</button>

                                <button className="btn btn-success btn-small" onClick={this.handleInviteClick}>
                                    { t('Invite') }

                                    {
                                        this.props.permissions && <UserPermissionSelector
                                            successText={ t('Invite') }
                                            permissions={this.props.permissions}
                                            onSuccess={this.handlePermissionSelect}
                                            provideController={this.setInvitePermissionCtrl}
                                        />
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                );
            }

            return (
                <div className="user-selector-nomatch">
                    { this.props.noMatchText }
                </div>
            );
        }

        const currentUserId = getCurrentUserId();

        return (
            <div className="user-selector-holder">
                <div className="user-selector">
                    <SelectionList onSelect={this.handleSuggestionSelect} active={this.state.focused}>
                        {
                            this.state.filteredSuggestions
                                .map(user => 
                                    <SelectionListItem key={user.id}>
                                        <UserEntry
                                            user={user}
                                            permissions={this.props.permissions}
                                            isSelf={user.id === currentUserId}
                                            t={t}
                                            onClick={this.handleSuggestionClick}
                                            onPermissionSelect={this.handlePermissionSelect}
                                            setPermissionCtrl={this.setPermissionCtrl}
                                        />
                                    </SelectionListItem>
                                )
                        }
                    </SelectionList>
                </div>
            </div>
        );
    }

    componentDidMount() {
        this.props.provideController && this.props.provideController({
            focus: () => {
                this.fieldCtrl && this.fieldCtrl.focus();
            },
            hide: () => {
                this.hideSuggestions();
            },
            reset: () => {
                this.selectedUser = null;
                this.email = '';
                this.setState({
                    searchTerm: '',
                    name: ''
                });
            }
        });

        this.updateFilteredSuggestions();
    }

    componentDidUpdate(prevProps) {
        if (this.props.email !== prevProps.email && this.props.email !== this.state.searchTerm) {
            this.setState({
                searchTerm: this.props.email
            });
        }

        if (prevProps.suggestions !== this.props.suggestions) {
            this.updateFilteredSuggestions();
        }
    }

    componentWillUnmount() {
        this.props.provideController && this.props.provideController(null);
    }

    render() {
        const { t } = this.props;
        return (
            <div className={'field-user field-holder' + (this.props.withName ? ' with-name' : '') + (this.nameIsEmail ? ' error' : '')}>
                <UserSearchField
                    value={this.state.searchTerm}
                    role={this.props.role || (this.roles && this.roles[0].id)}
                    roles={this.roles}
                    error={this.state.error}
                    roleSelectorDisabled={this.props.roleSelectorDisabled || (this.selectedUser && this.selectedUser.email === this.email) || !this.roles || this.roles.length <= 1}
                    placeholder={this.props.placeholder}
                    onChange={this.handleChange}
                    onRoleChange={this.handleRoleChange}
                    onFocus={this.handleFocus}
                    onBlur={this.handleBlur}
                    onKeyDown={this.handleKeyDown}
                    provideController={ctrl => this.fieldCtrl = ctrl}
                />

                {
                    this.props.withName ? (
                        <FieldDefault disabled={this.selectedUser && this.selectedUser.email === this.email} value={this.selectedUser ? this.selectedUser.name || '' : this.state.name} onChange={this.handleNameChange} placeholder={this.props.namePlaceholder} error={this.nameIsEmail} />
                    ) : null
                }

                {
                    this.nameIsEmail ? <small className="field-error">{t('settings:invite.Please enter a valid name for the invitee')}</small> : null
                }

                {
                    this.props.suggestions || this.props.noMatchText ? (
                        this.props.suggestionsPopout ? (
                            <Popout
                                className={'medium ' + this.props.popoutClassName}
                                xAlign="left"
                                closeOnClickOut={false}
                                provideController={this.setPopoutCtrl}
                            >
                                <div className="user-selector-chooser">
                                    <div className="popout-body">
                                        { this.suggestionsElement }
                                    </div>

                                    {
                                        this.props.popoutFoot.enabled && this.state.searchTerm ? (
                                            <div className="popout-foot">
                                                <div className={'create-user-entry' + (!this.validateNewUserEmail(this.state.searchTerm) ? ' error' : '') }>
                                                    <div className="user-entry-head">
                                                        <i className="icon material-icons">email</i>
                                                    </div>

                                                    <div className="user-entry-body">
                                                        <small>{this.state.searchTerm}</small>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : null
                                    }
                                </div>
                            </Popout>
                        ) : (
                            this.suggestionsElement
                        )
                    ) : null
                }
            </div>
        );
    }
}

export default withTranslation(['common', 'settings'])(FieldUser);